import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { AniListProvider, useAniList } from './context/AniListContext';
import { AnimeSourceProvider, useAnimeSource } from './context/AnimeSourceContext';
import { MangaListProvider } from './context/MangaListContext';
import { MangaSourceProvider, useMangaSource } from './context/MangaSourceContext';
import { BatchOperationProvider } from './context/BatchOperationContext';
import { TimeSettingsProvider } from './context/TimeSettingsContext';
import { TitlePreferenceProvider } from './context/TitlePreferenceContext';
import { LinkSettingsProvider } from './context/LinkSettingsContext';
import { UndoRedoProvider } from './context/UndoRedoContext';
import { AlertProvider } from './context/ToastContext';
import { AdultContentProvider } from './context/AdultContentContext';
import DatabaseInitializer from './components/DatabaseInitializer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Collections from './components/Collections';
import RelatedAnime from './components/RelatedAnime';
import Search from './components/Search';
import MarathonMode from './components/MarathonMode';
import QuickWins from './components/QuickWins';
import MyStats from './components/MyStats';
import Settings from './components/Settings';
import Compare from './components/Compare';
import OAuthCallback from './components/OAuthCallback';
import MangaDashboard from './components/MangaDashboard';
import MangaCollections from './components/MangaCollections';
import MangaSearch from './components/MangaSearch';
import MangaMarathonMode from './components/MangaMarathonMode';
import MangaQuickWins from './components/MangaQuickWins';
import MangaStats from './components/MangaStats';
import MangaRelated from './components/MangaRelated';
import MangaCompare from './components/MangaCompare';
import Responses from './components/Responses';

import anilistSyncService from './services/anilistSyncService';
import { getSimpleCache } from './services/cacheService';
import { getRemovedAnimeIds, initRemovedAnimeIds } from './services/removedAnimeService';
import { getRemovedMangaIds, initRemovedMangaIds } from './services/removedMangaService';
import apiService from './services/apiService';
import { MASTER_TABS, ANIME_TABS, MANGA_TABS, TABS, LOCAL_STORAGE_KEYS } from './config/constants';
import './App.css';

// Protected routes wrapper
const ProtectedRoutes = ({ children }) => {
  const { isConnected, isLoading } = useAnimeSource(); // Use unified source context
  const location = useLocation();

  // Allow OAuth callback routes even when not connected
  const isOAuthCallback = location.pathname === '/oauth-callback';

  // Wait for initialization before deciding — prevents redirect during cache loading
  if (isLoading && !isConnected && !isOAuthCallback) {
    return null; // Don't redirect yet, still loading from cache
  }

  if (!isConnected && !isOAuthCallback) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * User Data Synchronizer - Fetches all user data from server on login
 * This ensures cross-browser consistency by syncing server data to client
 */
const UserDataSynchronizer = () => {
  const { isConnected, username } = useAnimeSource();
  // useRef survives React Strict Mode's unmount/remount cycle (unlike useState)
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    // Guard: skip if already synced, not connected, or no username
    if (!isConnected || hasSyncedRef.current || !username) return;

    // Mark as synced immediately to prevent duplicate runs
    hasSyncedRef.current = true;

    const syncUserDataFromServer = async () => {
      try {
        console.log(`🔄 Syncing all user data from server for ${username}...`);

        // Import services dynamically to avoid circular dependencies
        const sqliteService = (await import('./services/sqliteService')).default;
        const { setUserItem } = (await import('./utils/userStorage'));

        // 1. Fetch all user data from server (public endpoint, no auth needed)
        const response = await apiService.getAllUserData(username);

        if (response?.found) {
          const { settings, watchLinks, removedAnime } = response;

          // 2. Sync settings (theme, preferences, etc.)
          if (settings) {
            Object.entries(settings).forEach(([key, value]) => {
              // Apply settings to localStorage
              if (key === 'theme') {
                localStorage.setItem('theme', value);

                // Apply theme with a small delay to ensure DOM is ready
                setTimeout(() => {
                  document.documentElement.setAttribute('data-theme', value);
                  console.log(`🎨 Theme applied: ${value}`);

                  // Force a re-render of components that depend on theme
                  window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: value } }));
                }, 100);
              } else if (key === 'anime_name_preference') {
                localStorage.setItem('anime_name_preference', value);
              } else if (key === 'time_settings_hours_per_day') {
                localStorage.setItem('time_settings_hours_per_day', value);
              } else if (key === 'time_settings_quick_win_hours') {
                localStorage.setItem('time_settings_quick_win_hours', value);
              }

              // Also save to user-specific storage
              try {
                setUserItem(key, JSON.stringify(value));
              } catch (error) {
                console.warn('Failed to save setting to user storage:', error);
              }
            });
          }

          // 3. Sync watch links
          if (watchLinks) {
            try {
              // Save to IndexedDB (same key LinkSettingsContext uses)
              await sqliteService.saveUserSetting('watch_links', watchLinks);
              // Also save to localStorage so LinkSettingsContext picks it up
              localStorage.setItem('external_watch_links', JSON.stringify(watchLinks));
              console.log(`📥 Synced ${watchLinks.length} watch links from server`);
            } catch (error) {
              console.warn('Failed to sync watch links:', error);
            }
          }

          // 4. Sync removed anime - save directly to local IndexedDB only
          // Do NOT call addRemovedAnime() which POSTs back to server (causing 401s).
          // The data already exists on the server; we're just pulling it to this browser.
          if (removedAnime?.ids && removedAnime.ids.length > 0) {
            console.log(`📥 Found ${removedAnime.ids.length} removed anime on server, syncing to client...`);

            try {
              // Clear local and batch-add from server (local-only, no server calls)
              await sqliteService.clearAllRemovedAnime();
              await sqliteService.batchAddRemovedAnime(removedAnime.ids);

              // Refresh removedAnimeService's in-memory cache from IndexedDB
              // This is critical — UI components read from this cache, not IndexedDB directly
              const { initRemovedAnimeIds } = await import('./services/removedAnimeService');
              await initRemovedAnimeIds();

              // Force refresh to update UI
              window.dispatchEvent(new Event('removedAnimeChanged'));
              console.log(`✅ Synced ${removedAnime.ids.length} removed anime to local storage`);
            } catch (error) {
              console.error('Failed to sync removed anime:', error);
            }
          }

          console.log('✅ User data sync complete');
        }
      } catch (error) {
        console.error('❌ User data sync failed:', error);
        // Allow retry on next mount by resetting ref
        hasSyncedRef.current = false;
      }
    };

    syncUserDataFromServer();
  }, [isConnected, username]);

  return null; // This component doesn't render anything
};




function AppContent() {
  const { isConnected, username, refreshData, updateUserDataDirect, isLoading, pendingAnime, userData } = useAnimeSource();
  const { updateMangaDataDirect, pendingManga } = useMangaSource();
  const location = useLocation();

  // Track admin responses count for the conditional Responses tab
  const [responseCount, setResponseCount] = useState(0);
  const [unreadResponseCount, setUnreadResponseCount] = useState(0);

  useEffect(() => {
    if (!isConnected || !username) return;
    const checkResponses = async () => {
      try {
        const data = await apiService.getUserResponses(username);
        setResponseCount(data.count || 0);
        // Only update badge if user is NOT on the Responses page (they've already read it there)
        if (!window.location.pathname.startsWith('/responses')) {
          setUnreadResponseCount(data.unreadCount || 0);
        }
      } catch (e) { /* silent */ }
    };
    checkResponses();
    // Poll every 15 seconds for near-instant badge updates
    const interval = setInterval(checkResponses, 15000);
    return () => clearInterval(interval);
  }, [isConnected, username]);

  // Determine which master tab is active
  const activeMasterTab = useMemo(() => {
    if (location.pathname.startsWith('/manga')) return 'manga';
    if (location.pathname.startsWith('/settings')) return 'settings';
    if (location.pathname.startsWith('/responses')) return 'responses';
    return 'anime'; // default
  }, [location.pathname]);

  // Clear unread badge when user views the Responses page — persist on server
  useEffect(() => {
    if (activeMasterTab === 'responses' && username) {
      setUnreadResponseCount(0);
      apiService.markResponsesAsRead(username);
    }
  }, [activeMasterTab, username]);

  // Track removed anime IDs for the Related Anime badge count
  const [removedAnimeVersion, setRemovedAnimeVersion] = useState(0);
  useEffect(() => {
    initRemovedAnimeIds().then(() => setRemovedAnimeVersion(v => v + 1));

    // Listen for changes from removedAnimeService (add/remove/restore)
    const handleRemovedChange = () => setRemovedAnimeVersion(v => v + 1);
    window.addEventListener('removedAnimeChanged', handleRemovedChange);
    return () => window.removeEventListener('removedAnimeChanged', handleRemovedChange);
  }, []);

  // Count of new (non-removed) related anime for the nav badge
  const newRelatedCount = useMemo(() => {
    if (removedAnimeVersion === 0 || !pendingAnime || pendingAnime.length === 0) return 0;
    const removedIds = getRemovedAnimeIds();
    return pendingAnime.filter(a => !removedIds.has(a.id)).length;
  }, [pendingAnime, removedAnimeVersion]);

  // Track removed manga IDs for the Related Manga badge count
  const [removedMangaVersion, setRemovedMangaVersion] = useState(0);
  useEffect(() => {
    initRemovedMangaIds().then(() => setRemovedMangaVersion(v => v + 1));

    const handleRemovedMangaChange = () => setRemovedMangaVersion(v => v + 1);
    window.addEventListener('removedMangaChanged', handleRemovedMangaChange);
    return () => window.removeEventListener('removedMangaChanged', handleRemovedMangaChange);
  }, []);

  // Count of new (non-removed) related manga for the nav badge
  const newRelatedMangaCount = useMemo(() => {
    if (removedMangaVersion === 0 || !pendingManga || pendingManga.length === 0) return 0;
    const removedIds = getRemovedMangaIds();
    return pendingManga.filter(m => !removedIds.has(m.id)).length;
  }, [pendingManga, removedMangaVersion]);

  // Scroll position restoration - save/restore scroll for each route
  useEffect(() => {
    const scrollPositions = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');

    // Restore scroll position for the current route
    const savedPosition = scrollPositions[location.pathname];

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (savedPosition !== undefined) {
          window.scrollTo(0, savedPosition);
        } else {
          window.scrollTo(0, 0); // Reset to top for new routes
        }
      });
    });

    // Save scroll position periodically while on this route
    let scrollTimeout;
    const saveScrollPosition = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const positions = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
        positions[location.pathname] = window.scrollY;
        sessionStorage.setItem('scrollPositions', JSON.stringify(positions));
      }, 100);
    };

    window.addEventListener('scroll', saveScrollPosition, { passive: true });

    // Save scroll position before unmounting
    return () => {
      window.removeEventListener('scroll', saveScrollPosition);
      clearTimeout(scrollTimeout);
      const positions = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}');
      positions[location.pathname] = window.scrollY;
      sessionStorage.setItem('scrollPositions', JSON.stringify(positions));
    };
  }, [location.pathname]);

  // Database is initialized by DatabaseInitializer wrapper — no duplicate init needed

  useEffect(() => {
    // Set initial theme
    const savedTheme = localStorage.getItem('theme') || 'fire';
    document.documentElement.setAttribute('data-theme', savedTheme);

    // Setup sync intervals for AniList
    if (isConnected) {
      const accessToken = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
      let lastSyncData = null; // Store last anime sync data for comparison
      let lastMangaSyncData = null; // Store last manga sync data for comparison
      let syncCount = 0; // Track sync count for periodic forced rebuild

      // Combined sync - runs anime sync → manga sync sequentially every 60s
      // Only 2 API calls per cycle (well within 90 req/min rate limit)
      const runCombinedSync = async () => {
        syncCount++;

        // ── Phase 1: Anime list sync ──
        try {
          console.log(`🔄 [Sync #${syncCount}] Auto-syncing anime list...`);
           await anilistSyncService.syncUserList(username, accessToken, (syncedData) => {
            const allEntries = syncedData.lists.flatMap(l => l.entries);
            console.log(`📥 Anime sync complete (${allEntries.length} entries)`);

            // Detect changes — compare with last sync OR cached data
            let hasRealChanges = false;
            const compareTarget = lastSyncData;

            if (!compareTarget) {
              // First sync: check if we actually have cached data displayed
              if (!userData) {
                // No cached data loaded (IndexedDB was empty/wiped) — treat as real update
                console.log('   ⚠️ First sync AND no cached userData — populating UI with synced data');
                hasRealChanges = true;
              } else {
                // Cache is loaded and displayed — just store as baseline for future comparisons
                console.log('   ℹ️ First sync — storing as baseline (no UI refresh needed, cache is current)');
              }
            } else {
              const oldEntries = compareTarget.lists.flatMap(l => l.entries);

              if (oldEntries.length !== allEntries.length) {
                hasRealChanges = true;
                console.log(`   📊 Anime count changed: ${oldEntries.length} → ${allEntries.length}`);
              } else {
                const oldMap = new Map(oldEntries.map(e => [e.media.id, e.updatedAt]));
                const changedEntries = allEntries.filter(e => oldMap.get(e.media.id) !== e.updatedAt);

                if (changedEntries.length > 0) {
                  hasRealChanges = true;
                  console.log(`   🔄 ${changedEntries.length} anime entries changed`);
                } else {
                  console.log('   ✅ No anime changes detected - skipping refresh');
                }
              }
            }

            // Force rebuild every 5th sync to pick up new catalog relations
            let isPeriodicRebuild = false;
            if (!hasRealChanges && syncCount % 5 === 0) {
              isPeriodicRebuild = true;
              console.log(`   🔄 Periodic rebuild (sync #${syncCount}) - refreshing groups for new catalog relations`);
            }

            if (hasRealChanges || isPeriodicRebuild) {
              updateUserDataDirect(syncedData, isPeriodicRebuild && !hasRealChanges);
            }

            lastSyncData = syncedData;
          });
        } catch (err) {
          console.error('❌ Anime sync failed:', err);
        }

        // ── Phase 2: Manga list sync ──
        try {
          console.log(`📖 [Sync #${syncCount}] Auto-syncing manga list...`);
          await anilistSyncService.syncUserMangaList(username, accessToken, (syncedData) => {
            const allEntries = syncedData.lists.flatMap(l => l.entries);
            console.log(`📥 Manga sync complete (${allEntries.length} entries)`);

            let hasChanges = false;
            if (!lastMangaSyncData) {
              // First manga sync: don't blindly refresh, cache is already displayed
              console.log('   ℹ️ First manga sync — storing as baseline');
            } else {
              const oldEntries = lastMangaSyncData.lists.flatMap(l => l.entries);
              if (oldEntries.length !== allEntries.length) {
                hasChanges = true;
              } else {
                const oldMap = new Map(oldEntries.map(e => [e.media.id, e.updatedAt]));
                hasChanges = allEntries.some(e => oldMap.get(e.media.id) !== e.updatedAt);
              }
            }

            if (hasChanges || syncCount % 5 === 0) {
              updateMangaDataDirect(syncedData, !hasChanges);
            }
            lastMangaSyncData = syncedData;
          });
        } catch (err) {
          console.error('❌ Manga sync failed:', err);
        }

        console.log(`✅ [Sync #${syncCount}] Combined anime + manga sync complete`);
      };

      // Single sync loop: anime → manga → wait 60s → repeat
      // First sync starts quickly (5s delay lets page finish rendering)
      // Subsequent syncs wait 60s between runs
      let cancelled = false;
      let syncTimeout;
      const syncLoop = async () => {
        await runCombinedSync();
        if (!cancelled) {
          syncTimeout = setTimeout(syncLoop, 60000);
        }
      };
      syncTimeout = setTimeout(syncLoop, 5000); // Start first sync after 5s (page settled)

      // Cleanup
      return () => {
        cancelled = true;
        clearTimeout(syncTimeout);
      };
    }
  }, [isConnected, refreshData, username]);

  const handleRefresh = () => {
    if (refreshData && !isLoading) {
      refreshData(true);
    }
  };

  // Get the current sub-tabs based on active master tab
  const currentSubTabs = activeMasterTab === 'manga' ? MANGA_TABS : ANIME_TABS;

  return (
    <div className="app">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={isConnected ? <Navigate to="/anime/dashboard" replace /> : <Login />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />

        {/* Legacy redirects: old paths → new /anime/ paths */}
        <Route path="/dashboard" element={<Navigate to="/anime/dashboard" replace />} />
        <Route path="/groups" element={<Navigate to="/anime/groups" replace />} />
        <Route path="/related" element={<Navigate to="/anime/related" replace />} />
        <Route path="/discover" element={<Navigate to="/anime/discover" replace />} />
        <Route path="/marathon" element={<Navigate to="/anime/marathon" replace />} />
        <Route path="/quick" element={<Navigate to="/anime/quick" replace />} />
        <Route path="/statistics" element={<Navigate to="/anime/statistics" replace />} />
        <Route path="/compare" element={<Navigate to="/anime/compare" replace />} />

        {/* Protected Routes */}
        <Route path="/*" element={
          <ProtectedRoutes>
            <>
              {/* User Data Synchronizer - ensures cross-browser data consistency */}
              <UserDataSynchronizer />

              {/* Master Tab Bar */}
              <nav className="master-nav">
                <div className="master-nav-container">
                  {MASTER_TABS.map(tab => {
                    // Insert Responses tab before Settings
                    if (tab.id === 'settings' && responseCount > 0) {
                      return (
                        <React.Fragment key="responses-and-settings">
                          <NavLink
                            to="/responses"
                            className={() => `master-tab ${activeMasterTab === 'responses' ? 'active' : ''}`}
                          >
                            💬 Responses
                            {unreadResponseCount > 0 && (
                              <span className="nav-tab-badge">{unreadResponseCount}</span>
                            )}
                          </NavLink>
                          <NavLink
                            to={tab.path}
                            className={() => `master-tab ${activeMasterTab === tab.id ? 'active' : ''}`}
                          >
                            {tab.name}
                          </NavLink>
                        </React.Fragment>
                      );
                    }
                    return (
                      <NavLink
                        key={tab.id}
                        to={tab.id === 'settings' ? tab.path : `${tab.path}/dashboard`}
                        className={() => `master-tab ${activeMasterTab === tab.id ? 'active' : ''}`}
                      >
                        {tab.name}
                        {tab.id === 'anime' && newRelatedCount > 0 && (
                          <span className="nav-tab-badge">{newRelatedCount}</span>
                        )}
                        {tab.id === 'manga' && newRelatedMangaCount > 0 && (
                          <span className="nav-tab-badge">{newRelatedMangaCount}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </nav>

              {/* Sub-Tab Bar (for Anime and Manga) */}
              {activeMasterTab !== 'settings' && activeMasterTab !== 'responses' && (
                <nav className="app-nav">
                  <div className="nav-container">
                    <div className="nav-tabs">
                      {currentSubTabs.map(tab => (
                        <NavLink
                          key={tab.id}
                          to={tab.path}
                          className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                        >
                          {tab.name}
                          {activeMasterTab === 'anime' && tab.id === 'related' && newRelatedCount > 0 && (
                            <span className="nav-tab-badge">{newRelatedCount}</span>
                          )}
                          {activeMasterTab === 'manga' && tab.id === 'related' && newRelatedMangaCount > 0 && (
                            <span className="nav-tab-badge">{newRelatedMangaCount}</span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                    <button
                      onClick={handleRefresh}
                      className="refresh-btn"
                      disabled={isLoading}
                      title="Refresh data from AniList"
                    >
                      {isLoading ? '🔄 Refreshing...' : '🔄 Refresh'}
                    </button>
                  </div>
                </nav>
              )}

              {/* Main Content */}
              <main className="app-main">
                <Routes>
                  <Route path="/" element={<Navigate to="/anime/dashboard" replace />} />

                  {/* Anime Routes */}
                  <Route path="/anime/dashboard" element={<Dashboard />} />
                  <Route path="/anime/groups" element={<Collections />} />
                  <Route path="/anime/related" element={<RelatedAnime />} />
                  <Route path="/anime/discover" element={<Search />} />
                  <Route path="/anime/marathon" element={<MarathonMode />} />
                  <Route path="/anime/quick" element={<QuickWins />} />
                  <Route path="/anime/statistics" element={<MyStats />} />
                  <Route path="/anime/compare" element={<Compare />} />

                  {/* Manga Routes */}
                  <Route path="/manga/dashboard" element={<MangaDashboard />} />
                  <Route path="/manga/groups" element={<MangaCollections />} />
                  <Route path="/manga/discover" element={<MangaSearch />} />
                  <Route path="/manga/marathon" element={<MangaMarathonMode />} />
                  <Route path="/manga/quick" element={<MangaQuickWins />} />
                  <Route path="/manga/statistics" element={<MangaStats />} />
                  <Route path="/manga/related" element={<MangaRelated />} />
                  <Route path="/manga/compare" element={<MangaCompare />} />

                  {/* Responses */}
                  <Route path="/responses" element={<Responses username={username} />} />

                  {/* Settings */}
                  <Route path="/settings" element={<Settings />} />
                </Routes>
              </main>


            </>
          </ProtectedRoutes>
        } />
      </Routes>
    </div>
  );
}


function App() {
  return (
    <DatabaseInitializer>
      <AlertProvider>
        <TitlePreferenceProvider>
          <LinkSettingsProvider>
            <TimeSettingsProvider>
              <UndoRedoProvider>
                <BatchOperationProvider>
                  <AniListProvider>
                    <AdultContentProvider>
                      <AnimeSourceProvider>
                        <MangaListProvider>
                          <MangaSourceProvider>
                            <Router>
                              <AppContent />
                            </Router>
                          </MangaSourceProvider>
                        </MangaListProvider>
                      </AnimeSourceProvider>
                    </AdultContentProvider>
                  </AniListProvider>
                </BatchOperationProvider>
              </UndoRedoProvider>
            </TimeSettingsProvider>
          </LinkSettingsProvider>
        </TitlePreferenceProvider>
      </AlertProvider>
    </DatabaseInitializer>
  );
}

export default App;
